import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { badgeToneDotBg, StatusBadge } from "components/ui/StatusBadge";
import { useSalesPipelines } from "hooks/use-sales-pipelines-api";
import { useTalentContracts } from "hooks/use-ses-contracts-api";
import type { TalentDetail } from "hooks/use-talents-api";
import { useCrmPermission } from "hooks/useCrmPermission";
import type { RestApiError } from "lib/rest-client";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TalentNotifySettingsTab } from "routes/crm/TalentDetailPage/TalentNotifySettingsTab";
import {
  formatYenToMan,
  getAffiliationLabel,
  getContractStatusBadge,
  getEmploymentTypeLabel,
  getRemotePreferenceLabel,
  getStageLabel,
  getStageTone,
  getWorkStatusBadge,
} from "routes/crm/utils";

type TabKey = "profile" | "pipeline" | "contract" | "activity" | "notify";

type TalentDetailPageProps = {
  talent: TalentDetail | null;
  loading: boolean;
  error: RestApiError | null;
};

/**
 * タレント詳細ページのプレゼンテーションコンポーネント
 * @param {object} props プロパティ
 * @param {object} props.talent タレント詳細データ
 * @param {boolean} props.loading ローディング状態
 * @param {object} props.error エラー
 * @return {React.ReactElement} タレント詳細ページ
 */
export const TalentDetailPage: React.FC<TalentDetailPageProps> = ({
  talent,
  loading,
  error,
}) => {
  const { canEdit } = useCrmPermission();
  const [activeTab, setActiveTab] = useState<TabKey>("profile");

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">
          <span>データの取得に失敗しました: {error.message}</span>
        </div>
      </div>
    );
  }

  if (!talent) {
    return (
      <div className="p-4">
        <div className="alert alert-warning">
          <span>タレントが見つかりません</span>
        </div>
      </div>
    );
  }

  const status = getWorkStatusBadge(talent.workStatus ?? "");

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/crm/talents">&larr; 一覧へ</Link>
          </Button>
          <h1 className="text-2xl font-bold">{talent.name}</h1>
          <span className="badge badge-outline">
            {getAffiliationLabel(talent.affiliation ?? "")}
          </span>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={`/settings/presets/new?fromTalentId=${talent.id}`}>
                プリセット作成
              </Link>
            </Button>
            <Button asChild size="sm">
              <Link to={`/crm/talents/${talent.id}/edit`}>編集</Link>
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div role="tablist" className="tabs tabs-bordered mb-4">
        <button
          type="button"
          role="tab"
          className={`tab ${activeTab === "profile" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          プロフィール
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${activeTab === "pipeline" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("pipeline")}
        >
          営業進捗
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${activeTab === "contract" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("contract")}
        >
          契約
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${activeTab === "activity" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("activity")}
        >
          活動ログ
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${activeTab === "notify" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("notify")}
        >
          🔔 通知設定
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "profile" && (
        <div>
          {/* Basic info grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h2 className="card-title text-base">基本情報</h2>
                <dl className="space-y-2 text-sm">
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">名前</dt>
                    <dd>{talent.name}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">フリガナ</dt>
                    <dd>{talent.nameKana || "-"}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">メール</dt>
                    <dd>{talent.email || "-"}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">電話番号</dt>
                    <dd>{talent.phone || "-"}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">年齢</dt>
                    <dd>{talent.age != null ? `${talent.age}歳` : "-"}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">担当者</dt>
                    <dd>{talent.assigneeName || "-"}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h2 className="card-title text-base">所属・条件</h2>
                <dl className="space-y-2 text-sm">
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">所属</dt>
                    <dd>{getAffiliationLabel(talent.affiliation ?? "")}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">雇用形態</dt>
                    <dd>
                      {getEmploymentTypeLabel(talent.employmentType ?? "")}
                    </dd>
                  </div>
                  {talent.affiliation === "partner" && (
                    <>
                      <div className="flex">
                        <dt className="w-32 text-base-content/60">
                          パートナー企業
                        </dt>
                        <dd>{talent.partnerCompany || "-"}</dd>
                      </div>
                      <div className="flex">
                        <dt className="w-32 text-base-content/60">
                          パートナー担当
                        </dt>
                        <dd>{talent.partnerContact || "-"}</dd>
                      </div>
                    </>
                  )}
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">最寄駅</dt>
                    <dd>{talent.nearestStation || "-"}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">リモート希望</dt>
                    <dd>
                      {getRemotePreferenceLabel(talent.remotePreference ?? "")}
                    </dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">希望単価</dt>
                    <dd>
                      {talent.desiredReward != null
                        ? `${talent.desiredReward.toLocaleString()}円`
                        : "-"}
                    </dd>
                  </div>
                  <div className="flex">
                    <dt className="w-32 text-base-content/60">稼働可能日</dt>
                    <dd>{talent.availableFrom || "-"}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="card bg-base-100 shadow-sm mt-4">
            <div className="card-body">
              <h2 className="card-title text-base">スキル</h2>
              <div className="flex flex-wrap gap-2">
                {talent.skills.length > 0 ? (
                  talent.skills.map(skill => (
                    <span key={skill.id} className="badge badge-outline">
                      {skill.skillName}
                      {skill.experienceYears != null &&
                        ` (${skill.experienceYears}年)`}
                    </span>
                  ))
                ) : (
                  <span className="text-base-content/60">
                    スキルが登録されていません
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {talent.notes && (
            <div className="card bg-base-100 shadow-sm mt-4">
              <div className="card-body">
                <h2 className="card-title text-base">メモ</h2>
                <p className="whitespace-pre-wrap text-sm">{talent.notes}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "pipeline" && <TalentPipelineTab talentId={talent.id} />}

      {activeTab === "contract" && <TalentContractTab talentId={talent.id} />}

      {activeTab === "activity" && <TalentActivityTab talentId={talent.id} />}

      {activeTab === "notify" && (
        <TalentNotifySettingsTab talentId={talent.id} canEdit={canEdit} />
      )}
    </div>
  );
};

// ================================================================
// パイプラインタブ（タレント詳細内）
// ================================================================

type TalentSubTabProps = {
  talentId: string;
};

/**
 * タレント詳細のパイプラインタブ
 * @param {object} props プロパティ
 * @param {string} props.talentId タレントID
 * @return {React.ReactElement} パイプラインタブ
 */
const TalentPipelineTab: React.FC<TalentSubTabProps> = ({ talentId }) => {
  const { pipelines, loading, error } = useSalesPipelines({
    talentId,
    limit: 50,
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>営業進捗の取得に失敗しました</span>
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body text-center text-base-content/60">
          営業進捗がありません
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>案件</th>
              <th>ステージ</th>
              <th>提案単価</th>
              <th>次アクション</th>
              <th>担当</th>
            </tr>
          </thead>
          <tbody>
            {pipelines.map(p => (
              <tr key={p.id} className="hover">
                <td>
                  {p.focusMatterTitle ||
                    p.matterTitle ||
                    p.clientCompany ||
                    "-"}
                </td>
                <td>
                  <StatusBadge tone={getStageTone(p.stage)}>
                    {getStageLabel(p.stage)}
                  </StatusBadge>
                </td>
                <td>
                  {p.proposedReward != null
                    ? formatYenToMan(p.proposedReward)
                    : "-"}
                </td>
                <td className="text-xs">
                  {p.nextActionDate ? (
                    <div>
                      <div>{p.nextActionDate}</div>
                      {p.nextActionMemo && (
                        <div className="text-base-content/60">
                          {p.nextActionMemo}
                        </div>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td>{p.assigneeName || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ================================================================
// 契約タブ（タレント詳細内）
// ================================================================

/**
 * タレント詳細の契約タブ
 * @param {object} props プロパティ
 * @param {string} props.talentId タレントID
 * @return {React.ReactElement} 契約タブ
 */
const TalentContractTab: React.FC<TalentSubTabProps> = ({ talentId }) => {
  const { contracts, loading, error } = useTalentContracts(talentId);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>契約情報の取得に失敗しました</span>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body text-center text-base-content/60">
          契約がありません
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>契約先</th>
              <th>案件名</th>
              <th>種別</th>
              <th>単価</th>
              <th>マージン</th>
              <th>期間</th>
              <th>ステータス</th>
              <th>更新回数</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map(c => {
              const statusLabel =
                c.status === "active"
                  ? "稼働中"
                  : c.status === "renewal_pending"
                    ? "更新待ち"
                    : c.status === "ended"
                      ? "終了"
                      : c.status === "renewed"
                        ? "更新済"
                        : c.status;
              const statusBadge = getContractStatusBadge(c.status);
              return (
                <tr key={c.id} className="hover">
                  <td>{c.clientCompany || "-"}</td>
                  <td>{c.projectName || "-"}</td>
                  <td className="text-xs">{c.contractType}</td>
                  <td>{c.reward != null ? `${c.reward}万円` : "-"}</td>
                  <td>{c.margin != null ? `${c.margin}万円` : "-"}</td>
                  <td className="text-xs">
                    {c.startDate}
                    {c.endDate ? ` ~ ${c.endDate}` : " ~"}
                  </td>
                  <td>
                    <StatusBadge tone={statusBadge.tone}>
                      {statusLabel}
                    </StatusBadge>
                  </td>
                  <td>{c.renewalCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ================================================================
// 活動ログタブ（タレント詳細内）
// ================================================================

/**
 * タレント詳細の活動ログタブ
 * パイプラインと契約のイベントからタイムラインを自動生成する
 * @param {object} props プロパティ
 * @param {string} props.talentId タレントID
 * @return {React.ReactElement} 活動ログタブ
 */
const TalentActivityTab: React.FC<TalentSubTabProps> = ({ talentId }) => {
  const { pipelines } = useSalesPipelines({ talentId, limit: 100 });
  const { contracts } = useTalentContracts(talentId);

  type ActivityItem = {
    date: string;
    type: "pipeline" | "contract";
    description: string;
  };

  const [activities, setActivities] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const items: ActivityItem[] = [];

    for (const p of pipelines) {
      items.push({
        date: p.stageChangedAt || p.createdAt,
        type: "pipeline",
        description: `[${getStageLabel(p.stage)}] ${p.focusMatterTitle || p.matterTitle || p.clientCompany || "案件"}`,
      });
    }

    for (const c of contracts) {
      const statusLabel =
        c.status === "active"
          ? "契約開始"
          : c.status === "renewed"
            ? "契約更新"
            : c.status === "ended"
              ? "契約終了"
              : c.status;
      items.push({
        date: c.createdAt,
        type: "contract",
        description:
          `[${statusLabel}] ${c.clientCompany || ""} ${c.projectName || ""}`.trim(),
      });
    }

    items.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    setActivities(items);
  }, [pipelines, contracts]);

  if (activities.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body text-center text-base-content/60">
          活動ログがありません
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <ul className="timeline timeline-vertical timeline-compact">
          {activities.map((activity, i) => (
            <li key={`${activity.date}-${i}`}>
              {i > 0 && <hr />}
              <div className="timeline-start text-xs text-base-content/60">
                {new Date(activity.date).toLocaleDateString("ja-JP")}
              </div>
              <div className="timeline-middle">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    activity.type === "contract"
                      ? badgeToneDotBg["critical-success"]
                      : badgeToneDotBg["stage-4"]
                  }`}
                />
              </div>
              <div className="timeline-end text-sm pb-4">
                {activity.description}
              </div>
              {i < activities.length - 1 && <hr />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
